import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, ChevronLeft, MapPin, Users, CheckCircle2, TrendingUp } from "lucide-react";
import Seo from "@/components/Seo";

const PATH = "/blog/case-study-bali-350-pax-channel-partner-launch";
const TITLE = "Case Study: A 350-Pax Channel Partner Launch in Bali";
const DESCRIPTION =
  "How Trawelldmc operated a 350-pax channel-partner product launch across Nusa Dua — venue split, beach gala, content capture, and a 4× engagement uplift versus prior years.";
const PUBLISHED_ISO = "2026-02-17";
const PUBLISHED_LABEL = "Feb 17, 2026";
const AUTHOR = "Trawelldmc Team";
const IMAGE =
  "https://images.unsplash.com/photo-1537996194471-e657df975ab4?crop=entropy&cs=srgb&fm=webp&w=1600&q=80";

const BREADCRUMB = [
  { name: "Home", path: "/" },
  { name: "Blog", path: "/blog" },
  { name: "Case Study: 350-Pax Bali Launch", path: PATH },
];

const ARTICLE_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: TITLE,
  description: DESCRIPTION,
  image: IMAGE,
  datePublished: PUBLISHED_ISO,
  dateModified: PUBLISHED_ISO,
  author: { "@type": "Organization", name: AUTHOR, url: "https://www.trawelldmc.com" },
  publisher: {
    "@type": "Organization",
    name: "Trawelldmc",
    logo: { "@type": "ImageObject", url: "https://www.trawelldmc.com/trawelldmc-logo-hd.png" },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `https://www.trawelldmc.com${PATH}` },
};

const KPIS = [
  { label: "Channel Partners", value: "350" },
  { label: "Duration", value: "4 days" },
  { label: "Content Reach", value: "1.2M" },
  { label: "Engagement", value: "4× prior" },
];

export default function CaseStudyBaliLaunch() {
  return (
    <div data-testid="case-study-bali-launch">
      <Seo
        path={PATH}
        title={`${TITLE} | Trawelldmc Case Study`}
        description={DESCRIPTION}
        type="article"
        image={IMAGE}
        breadcrumb={BREADCRUMB}
        extraJsonLd={ARTICLE_JSONLD}
      />

      <section className="bg-[#0B192C] text-white">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-8">
            <ChevronLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <div className="overline !text-[#FF5238] mb-5">Case Study · Bali DMC</div>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.08]">
            {TITLE}
          </h1>
          <div className="mt-8 flex items-center gap-5 text-sm text-white/60 flex-wrap">
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {PUBLISHED_LABEL}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 7 min read</span>
            <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Nusa Dua, Bali</span>
            <span>By {AUTHOR}</span>
          </div>
        </div>
      </section>

      <section>
        <div className="max-w-5xl mx-auto px-6 md:px-10 -mt-10 mb-12">
          <img
            src={IMAGE}
            alt="Nusa Dua resort gala dinner Bali — Trawelldmc 350-pax channel partner case study"
            width="1600"
            height="900"
            loading="lazy"
            decoding="async"
            className="w-full aspect-[16/9] object-cover rounded-md shadow-xl"
          />
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-10 mb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {KPIS.map((k) => (
            <div key={k.label} className="card-flat p-6 text-center">
              <div className="font-heading text-3xl font-light text-[#FF5238]">{k.value}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">{k.label}</div>
            </div>
          ))}
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 md:px-10 pb-24">
        <p className="text-xl text-slate-700 leading-relaxed font-light">
          A consumer-electronics brand needed to unveil their next-generation
          product line to 350 top-performing channel partners — and make it
          feel utterly differentiated from the prior three annual launches
          held in Bangkok. Bali was the storyline. Trawelldmc was the operator.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">01.</span> The Brief
        </h2>
        <p className="text-slate-700 leading-relaxed">
          350 distributor principals and senior account managers, flown in
          from 14 markets across South Asia and the Middle East. A
          product-reveal plenary on Day 1, a partner-track conference on Day
          2, a leisure-and-content-capture Day 3, and an unforgettable gala on
          Day 4. The client wanted social content that channel partners would
          re-share for weeks afterwards.
        </p>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">02.</span> Architecture
        </h2>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-2 list-disc pl-6">
          <li><strong>Anchor property: The Apurva Kempinski Bali</strong> — 300 keys held as the primary inventory; ballroom hosted the reveal plenary.</li>
          <li><strong>Overflow: Sofitel Bali Nusa Dua Beach Resort</strong> — 50 keys for late-confirming partners; shuttle every 20 minutes during conference days.</li>
          <li><strong>Reveal stage</strong> — 22-meter LED canopy, lighting choreographed to the product film, simultaneous English &amp; Arabic interpretation.</li>
          <li><strong>Gala</strong> — beachfront at the Kempinski lagoon, plated Indonesian degustation, fire-dancers, and a 12-minute drone-light show finale.</li>
          <li><strong>Content capture</strong> — 6-person production team across 4 days; deliverables: 1 hero film, 8 partner testimonials, 90 stills, social-ready vertical cuts.</li>
        </ul>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">03.</span> Operational Wins
        </h2>
        <ul className="text-slate-700 leading-relaxed mt-4 space-y-3 list-none pl-0">
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Indonesian visa pre-clearance</strong> for all 350 pax handled 17 days before arrival — zero airport delays.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>F&amp;B differentiation</strong> — Halal-certified Indonesian menu signed off with each market&apos;s lead distributor, avoiding the &ldquo;same-food-every-year&rdquo; complaint.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Bali Hindu cultural welcome</strong> — traditional dance &amp; floral garland greeting at the resort entrance, content captured for partner social posts.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Sunrise Mount Batur trek</strong> for the top 40 performers — exclusive sub-experience, captured as a teaser for the next year&apos;s campaign.</span></li>
          <li className="flex gap-3"><CheckCircle2 className="h-5 w-5 text-[#FF5238] flex-shrink-0 mt-1" /><span><strong>Branded merchandise</strong> sourced through Balinese artisans — every partner left with a hand-carved keepsake, not generic swag.</span></li>
        </ul>

        <h2 className="font-heading text-3xl md:text-4xl font-light tracking-tight text-[#0B192C] mt-12 mb-5 flex items-center gap-3">
          <span className="text-[#FF5238]">04.</span> Results
        </h2>
        <div className="bg-[#FDFCF8] border-l-4 border-[#FF5238] p-7 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <TrendingUp className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">4× engagement</div>
              <div className="text-sm text-slate-600 mt-1">Partner-shared social content vs prior years</div>
            </div>
            <div>
              <Users className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">1.2M reach</div>
              <div className="text-sm text-slate-600 mt-1">Organic partner-channel impressions in 30 days</div>
            </div>
            <div>
              <CheckCircle2 className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">22% sell-in lift</div>
              <div className="text-sm text-slate-600 mt-1">Quarter-on-quarter new SKU pre-orders</div>
            </div>
            <div>
              <Calendar className="h-6 w-6 text-[#FF5238]" />
              <div className="font-heading text-2xl font-medium text-[#0B192C] mt-2">3-year deal</div>
              <div className="text-sm text-slate-600 mt-1">Trawelldmc retained as channel-event partner through 2028</div>
            </div>
          </div>
        </div>

        <p className="text-slate-700 leading-relaxed mt-8">
          What makes a channel-partner launch deliver business outcomes? Not a
          longer agenda. A more shareable destination, content that travels in
          partner pockets, and operational reliability that lets the marketing
          team focus on storytelling rather than logistics. Bali delivers all
          three when the DMC is paying attention.
        </p>

        <div className="mt-12 p-8 bg-[#0B192C] text-white rounded-md">
          <div className="overline !text-[#FF5238] mb-3">Brief us</div>
          <h3 className="font-heading text-2xl font-medium tracking-tight">
            Planning a launch your partners will share?
          </h3>
          <p className="text-white/80 mt-3 leading-relaxed">
            Talk to a senior account manager about Bali, Singapore, Dubai, or
            any of our 11 Asia markets. We respond within 48 hours.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/inquiry?destination=Bali&type=Product%20Launch" className="btn-primary inline-flex">
              Submit an RFP <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/bali-dmc" className="btn-secondary !bg-white/10 !border-white/20 !text-white hover:!bg-white/20">
              Read our Bali DMC services
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
